import { useCallback, useEffect, useRef, useState } from 'react'
import { onMetaWrittenElsewhere, readMeta, writeMeta } from './db'
import { type Consensus, OUTCOMES } from '../domain/types'

/**
 * Filters and view preferences, kept across visits.
 *
 * Losing your filters on refresh is as annoying as losing the data, so these
 * are persisted alongside the reports rather than held in memory. Writes are
 * debounced, because filter state changes on every keystroke.
 */

/**
 * The filters the Indicator results page kept under its own key before it
 * moved to the shared page filter. Kept only so saved ones, and ones in older
 * session files, can be read once and moved over (useMigratedFilters).
 */
export interface Filters {
  search: string
  outcomes: Consensus[]
  dimension: string | null
  project: string | null
  tool: string | null
  /** Only reports assessed on or after this day (YYYY-MM-DD). Absent in older saves. */
  from?: string | null
  /** Only reports assessed on or before this day. */
  to?: string | null
}

export const NO_FILTERS: Filters = {
  search: '',
  outcomes: [],
  dimension: null,
  project: null,
  tool: null,
  from: null,
  to: null,
}

/**
 * Told when the stored view state has been replaced wholesale.
 *
 * Importing a session rewrites the whole meta store underneath hooks that read
 * it once on mount. Without a nudge they would carry on showing the filters of
 * the session being replaced, and the file would look like it had restored only
 * half of itself. Reloading the page would also work, but throwing the document
 * away to re-read four small values is a heavy way to do it.
 */
const rehydrators = new Set<() => void>()

export function viewStateReplaced(): void {
  for (const rehydrate of [...rehydrators]) rehydrate()
}

/**
 * State that restores itself from storage and saves itself back.
 *
 * `restored` tells the caller whether the stored value has arrived yet, so the
 * interface can hold off rather than rendering a default and then visibly
 * replacing it.
 */
export function usePersistentState<T>(
  key: string,
  initial: T,
  {
    debounceMs = 250,
    validate,
  }: {
    debounceMs?: number
    /**
     * Whether a stored value can be used. Stored values may come from an
     * imported session file, which is untrusted, so one that fails is
     * replaced by the default. Without it, the value must at least have the
     * same shape as the default.
     */
    validate?: (value: unknown) => boolean
  } = {},
): [T, (next: T | ((current: T) => T)) => void, boolean] {
  const [value, setValue] = useState<T>(initial)
  const [restored, setRestored] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
  // The write the timer is waiting to make, so unmounting can make it at once
  // instead of losing the last change.
  const pendingWrite = useRef<() => void>(undefined)
  // Held in a ref so re-reading does not depend on the caller passing a stable
  // default; most pass an object literal, which differs on every render.
  const initialRef = useRef(initial)
  const validateRef = useRef(validate ?? sameShapeAs(initial))
  // Changes made before the first read from storage, which is asynchronous.
  // Replayed on top of the stored value when it lands, so neither undoes the
  // other: a project created early does not wipe the saved ones, and the saved
  // ones do not wipe it. Undefined once the first read is done.
  const early = useRef<((current: T) => T)[] | undefined>([])

  useEffect(() => {
    let cancelled = false

    const load = (replace: boolean) => {
      void readMeta<T>(key).then((stored) => {
        if (cancelled) return
        setRestored(true)
        // An imported session may not carry every key the previous one had, so
        // a missing value resets to the default rather than leaving the old
        // one in place and mixing two sessions together.
        const base =
          stored === undefined || !validateRef.current(stored) ? initialRef.current : stored
        const changes = replace ? [] : (early.current ?? [])
        early.current = undefined
        if (changes.length === 0) {
          setValue(base)
          return
        }
        const merged = changes.reduce((current, change) => change(current), base)
        setValue(merged)
        void writeMeta(key, merged)
      })
    }

    // A session import replaces every value, whatever the user has changed,
    // and so does another tab writing this same setting.
    const rehydrate = () => load(true)
    load(false)
    rehydrators.add(rehydrate)
    const stopListening = onMetaWrittenElsewhere((written) => {
      if (written === key) rehydrate()
    })
    return () => {
      cancelled = true
      rehydrators.delete(rehydrate)
      stopListening()
    }
  }, [key])

  const update = useCallback(
    (next: T | ((current: T) => T)) => {
      const change = typeof next === 'function' ? (next as (c: T) => T) : () => next
      if (early.current) {
        // Shown at once, saved once merged with what is stored.
        early.current.push(change)
        setValue(change)
        return
      }
      setValue((current) => {
        const resolved = change(current)
        clearTimeout(timer.current)
        const write = () => {
          pendingWrite.current = undefined
          void writeMeta(key, resolved)
        }
        pendingWrite.current = write
        timer.current = setTimeout(write, debounceMs)
        return resolved
      })
    },
    [key, debounceMs],
  )

  // Leaving a page, or closing or reloading the tab, within the debounce
  // would otherwise drop its last change. The write is started before the
  // page goes; the browser lets a transaction begun then complete.
  useEffect(() => {
    const flush = () => {
      clearTimeout(timer.current)
      pendingWrite.current?.()
    }
    window.addEventListener('pagehide', flush)
    return () => {
      window.removeEventListener('pagehide', flush)
      flush()
    }
  }, [])

  return [value, update, restored]
}

function shapeOf(value: unknown): string {
  if (value === null) return 'null'
  return Array.isArray(value) ? 'array' : typeof value
}

/**
 * The default check: a stored value is usable if it is the same kind of thing
 * as the default. A default of null stands for "nothing yet", so any scalar
 * is accepted in its place.
 */
function sameShapeAs(initial: unknown): (value: unknown) => boolean {
  const expected = shapeOf(initial)
  return (value) => {
    const actual = shapeOf(value)
    if (expected === 'null') return actual !== 'object' && actual !== 'array'
    return actual === expected
  }
}

const isTextOrNull = (v: unknown) => v === null || typeof v === 'string'

/**
 * Whether a stored value is a complete Filters object. Checked field by field:
 * an imported session can carry anything, and a filter missing a field breaks
 * every page that reads it.
 */
const isDayOrAbsent = (value: unknown) =>
  value === undefined ||
  value === null ||
  (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value))

export function isFilters(value: unknown): value is Filters {
  if (typeof value !== 'object' || value === null) return false
  const v = value as Record<string, unknown>
  return (
    typeof v.search === 'string' &&
    Array.isArray(v.outcomes) &&
    v.outcomes.every((o) => OUTCOMES.includes(o as Consensus)) &&
    isTextOrNull(v.dimension) &&
    isTextOrNull(v.project) &&
    isTextOrNull(v.tool) &&
    isDayOrAbsent(v.from) &&
    isDayOrAbsent(v.to)
  )
}
